import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { invoicesApi } from '../../api/invoices';
import { companyApi } from '../../api/company';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useReportScreen } from '../../components/ScreenContext';
import { useState, useRef, useMemo } from 'react';

export default function InvoiceDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const direction = searchParams.get('direction') ?? 'Made';
  const [showPostDialog, setShowPostDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice-detail', id, direction],
    queryFn: () => invoicesApi.getById(Number(id), direction),
  });

  const { data: company } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
  });

  // Report screen context to AI copilot
  const screenSummary = invoice
    ? `Saskaita faktura #${invoice.documentNumber} (${direction}), ${formatMoney(invoice.totalSum + invoice.totalVat)} ${invoice.currencyCode}${invoice.isPosted ? ' [Registruota]' : ' [Neregistruota]'}`
    : 'Saskaita faktura nerasta';
  const screenPreview = useMemo(() => invoice
    ? invoice.lines.slice(0, 5).map(l => `${l.name}: ${formatMoney(l.sum + l.sumVat)}`)
    : [], [invoice]);
  const activeRecord = useMemo(() => invoice ? {
    type: direction === 'Made' ? 'sales-invoice' : 'purchase-invoice',
    fields: {
      id: String(invoice.id),
      documentNumber: invoice.documentNumber,
      date: formatDate(invoice.date),
      person: invoice.personName ?? `ID: ${invoice.personId}`,
      content: invoice.content,
      currency: `${invoice.currencyCode} (rate: ${invoice.currencyRate})`,
      totalSum: formatMoney(invoice.totalSum),
      totalVat: formatMoney(invoice.totalVat),
      total: formatMoney(invoice.totalSum + invoice.totalVat),
      isPosted: invoice.isPosted ? 'Taip' : 'Ne',
      lineCount: String(invoice.lines.length),
    },
  } : null, [invoice, direction]);
  useReportScreen(screenSummary, {
    recordCount: invoice ? 1 : 0,
    preview: screenPreview,
    activeRecord: activeRecord ?? undefined,
  });

  const postMutation = useMutation({
    mutationFn: () => invoicesApi.post(Number(id), direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setShowPostDialog(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      direction === 'Made'
        ? invoicesApi.deleteMade(Number(id))
        : invoicesApi.deleteReceived(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      navigate('/invoices/made');
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (!invoice) return <div className="text-center py-8 text-gray-500">Invoice not found</div>;

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Invoice #{invoice.documentNumber}
          </h1>
          <p className="text-sm text-gray-500">
            {direction === 'Made' ? 'Sales (Made)' : 'Purchase (Received)'} | ID: {invoice.id}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="px-3 py-1.5 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200"
            onClick={() => navigate(`/invoices/${id}/edit?direction=${direction}`)}
          >
            Edit
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-amber-100 text-amber-700 rounded-md hover:bg-amber-200"
            onClick={() => {
              const state = { invoice };
              navigate(`/invoices/credit-note/new?linkedInvoiceId=${id}&direction=${direction}`, { state });
            }}
          >
            {t('invoices.createCreditNote')}
          </button>
          {invoice.isPosted ? (
            <span className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-md inline-flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Posted
            </span>
          ) : (
            <button
              className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200"
              onClick={() => setShowPostDialog(true)}
            >
              Post to Ledger
            </button>
          )}
          <button
            className="px-4 py-1.5 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 inline-flex items-center gap-1.5"
            onClick={() => setShowPreview(!showPreview)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Preview
          </button>
          <a
            className="px-4 py-1.5 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 inline-flex items-center gap-1.5"
            href={invoicesApi.getPdfUrl(Number(id), direction)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print / PDF
          </a>
          <button
            className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
            onClick={() => setShowDeleteDialog(true)}
          >
            Delete
          </button>
          <button
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>
      </div>

      {/* Invoice details card */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500 text-xs">Date</div>
            <div className="font-medium" data-ai-field="invoice-date">{formatDate(invoice.date)}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Document #</div>
            <div className="font-medium" data-ai-field="invoice-doc-number">{invoice.documentNumber}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">{direction === 'Made' ? 'Klientas' : 'Tiekėjas'}</div>
            <div className="font-medium" data-ai-field="invoice-person">{invoice.personName ?? `ID: ${invoice.personId}`}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Currency</div>
            <div className="font-medium" data-ai-field="invoice-currency">{invoice.currencyCode} (rate: {invoice.currencyRate})</div>
          </div>
        </div>

        <div>
          <div className="text-gray-500 text-xs">Content</div>
          <div className="text-sm" data-ai-field="invoice-content">{invoice.content}</div>
        </div>

        {/* Status indicator */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500 text-xs">Status:</span>
          {invoice.isPosted ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-700 rounded">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Registruota (Posted)
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-700 rounded">
              Neregistruota (Draft)
            </span>
          )}
        </div>
      </div>

      {/* Line items */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-2 border-b border-gray-200">
          <h2 className="font-semibold text-sm">Line Items</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-left">Name</th>
                <th className="text-right">Qty</th>
                <th className="text-left">Unit</th>
                <th className="text-right">Price</th>
                <th className="text-right">Account</th>
                <th className="text-right">VAT Rate</th>
                <th className="text-right">Sum</th>
                <th className="text-right">VAT</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line) => (
                <tr key={line.id}>
                  <td>{line.name}</td>
                  <td className="num">{line.quantity}</td>
                  <td>{line.unit}</td>
                  <td className="num">{formatMoney(line.unitPrice, false)}</td>
                  <td className="num">{line.account}</td>
                  <td className="num">{(line.vatRate * 100).toFixed(0)}%</td>
                  <td className="num">{formatMoney(line.sum)}</td>
                  <td className="num">{formatMoney(line.sumVat)}</td>
                  <td className="num font-medium">{formatMoney(line.sum + line.sumVat)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="text-right font-semibold">Total</td>
                <td className="num font-semibold">{formatMoney(invoice.totalSum)}</td>
                <td className="num font-semibold">{formatMoney(invoice.totalVat)}</td>
                <td className="num font-semibold">{formatMoney(invoice.totalSum + invoice.totalVat)}</td>
              </tr>
              {invoice.currencyCode !== 'EUR' && (
                <tr>
                  <td colSpan={6} className="text-right text-xs text-gray-500">Original currency</td>
                  <td className="num text-xs text-gray-500">{formatMoney(invoice.totalOriginal, false)}</td>
                  <td className="num text-xs text-gray-500">{formatMoney(invoice.totalVatOriginal, false)}</td>
                  <td className="num text-xs text-gray-500">{formatMoney(invoice.totalOriginal + invoice.totalVatOriginal, false)}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>

      {/* Invoice Preview (printable) */}
      {showPreview && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 print-area" ref={previewRef}>
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">INVOICE</h2>
              <p className="text-sm text-gray-500 mt-1">
                {direction === 'Made' ? 'Sales Invoice' : 'Purchase Invoice'}
              </p>
            </div>
            <div className="text-right text-sm">
              <div><span className="text-gray-500">Invoice #:</span> <strong>{invoice.documentNumber}</strong></div>
              <div><span className="text-gray-500">Date:</span> {formatDate(invoice.date)}</div>
              <div><span className="text-gray-500">Currency:</span> {invoice.currencyCode}</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
            <div className="bg-gray-50 rounded p-3">
              <div className="text-xs text-gray-500 uppercase font-semibold mb-1">From</div>
              {company ? (
                <div>
                  <div className="font-medium">{company.name}</div>
                  {company.code ? <div className="text-gray-600">Įmonės kodas: {company.code}</div> : null}
                  {company.vatCode ? <div className="text-gray-600">PVM kodas: {company.vatCode}</div> : null}
                  {company.address ? <div className="text-gray-600">{company.address}</div> : null}
                </div>
              ) : (
                <div className="text-gray-400">Company info not available</div>
              )}
            </div>
            <div className="bg-gray-50 rounded p-3">
              <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Bill To</div>
              <div>
                <div className="font-medium">{invoice.personName ?? `ID: ${invoice.personId}`}</div>
                {invoice.personCode ? <div className="text-gray-600">Įmonės kodas: {invoice.personCode}</div> : null}
                {invoice.personVatCode ? <div className="text-gray-600">PVM kodas: {invoice.personVatCode}</div> : null}
                {invoice.personAddress ? <div className="text-gray-600">{invoice.personAddress}</div> : null}
              </div>
            </div>
          </div>

          <table className="w-full text-sm mb-6">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-2 font-semibold">#</th>
                <th className="text-left py-2 font-semibold">Description</th>
                <th className="text-right py-2 font-semibold">Qty</th>
                <th className="text-right py-2 font-semibold">Unit Price</th>
                <th className="text-right py-2 font-semibold">VAT</th>
                <th className="text-right py-2 font-semibold">Total</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line, i) => (
                <tr key={line.id} className="border-b border-gray-100">
                  <td className="py-2 text-gray-500">{i + 1}</td>
                  <td className="py-2">{line.name}</td>
                  <td className="py-2 text-right">{line.quantity} {line.unit}</td>
                  <td className="py-2 text-right">{formatMoney(line.unitPrice, false)}</td>
                  <td className="py-2 text-right">{(line.vatRate * 100).toFixed(0)}%</td>
                  <td className="py-2 text-right font-medium">{formatMoney(line.sum + line.sumVat)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-gray-300">
                <td colSpan={5} className="py-2 text-right font-semibold">Subtotal</td>
                <td className="py-2 text-right">{formatMoney(invoice.totalSum)}</td>
              </tr>
              <tr>
                <td colSpan={5} className="py-2 text-right font-semibold">VAT</td>
                <td className="py-2 text-right">{formatMoney(invoice.totalVat)}</td>
              </tr>
              <tr className="bg-gray-50">
                <td colSpan={5} className="py-3 text-right text-lg font-bold">Total</td>
                <td className="py-3 text-right text-lg font-bold">{formatMoney(invoice.totalSum + invoice.totalVat)} {invoice.currencyCode}</td>
              </tr>
            </tfoot>
          </table>

          <div className="flex justify-end gap-2 mt-4 print:hidden">
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
              onClick={() => window.print()}
            >
              Print
            </button>
            <a
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 inline-flex items-center"
              href={invoicesApi.getPdfUrl(Number(id), direction)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Download PDF
            </a>
            <button
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
              onClick={() => setShowPreview(false)}
            >
              Close Preview
            </button>
          </div>
        </div>
      )}

      {/* Post dialog */}
      <ConfirmDialog
        open={showPostDialog}
        title="Post Invoice"
        message={`Post invoice #${invoice.documentNumber} to the general ledger? This will create a journal entry.`}
        confirmLabel="Post"
        onConfirm={() => postMutation.mutate()}
        onCancel={() => setShowPostDialog(false)}
      />

      {/* Delete dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        title="Delete Invoice"
        message={`Permanently delete invoice #${invoice.documentNumber}? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
}
